<template>
  <article
    :class="[$style.card, variant === 'carousel' && $style.carousel]"
    data-search-results-card-v="3.4.1"
  >
    <SearchResultsAsset
      v-if="type === 'product' || type === 'marketing-material'"
      :data="props.data as TypeResultAssetData"
    />

    <div
      v-else
      :class="[$style.content, computedLink && $style.contentLink]"
      @click="
        (e) =>
          computedItems?.length
            ? false
            : $handleBlockLinkClick(e, cardLinkEl?.$el)
      "
    >
      <div
        :class="[
          $style.thumbnail,
          computedFit === 'pad' && $style.thumbnailPadded,
        ]"
      >
        <AspectRatio ratio="317x224">
          <BasePicture
            v-if="computedImage?.fields?.file?.url"
            :src="`${computedImage?.fields?.file?.url}?fit=${computedFit}&q=90&w=634`"
            alt=""
          />
        </AspectRatio>
      </div>
      <div :class="$style.inner">
        <button
          v-if="computedItems?.length"
          :class="[$style.toggle, open && $style.toggleActive]"
          @click="handleToggleDrawer"
        >
          <IconChevronDown :class="$style.toggleIcon" />
        </button>
        <div :class="[$style.meta, 'u-type-p3']">
          {{ computedContentType }}
          <span v-if="computedReleaseDate">
            &mdash; {{ computedReleaseDate }}
          </span>
        </div>
        <BaseHeading as="h3" size="h6" :class="$style.title">
          <NuxtLinkLocale
            v-if="computedLink"
            ref="cardLink"
            :to="computedLink"
            :class="$style.titleLink"
            target="_blank"
          >
            {{ computedTitle }}
          </NuxtLinkLocale>
          <template v-else>
            {{ computedTitle }}
          </template>
        </BaseHeading>
        <MarkedParser :md="computedExcerpt" :class="$style.copy" />
        <EventCountdown
          v-if="computedStartDatetime && computedEndDatetime"
          :start-datetime="computedStartDatetime"
          :end-datetime="computedEndDatetime"
        />
        <CallToAction
          v-if="computedCta"
          :cta="computedCta"
          :class="$style.cta"
        />
      </div>
    </div>

    <details v-if="computedItems?.length" ref="details" :open="open">
      <summary :class="$style.summary">
        {{ computedTitle }}
      </summary>
      <div :class="$style.flow">
        <ul v-if="computedItems.length" :class="$style.assets">
          <li v-for="item in computedItems" :key="item?.id">
            <SearchResultsAsset :data="item" />
          </li>
        </ul>
      </div>
    </details>
  </article>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import type { Asset, Entry, EntrySkeletonType } from 'contentful'
import type {
  TypeArticleWithoutUnresolvableLinksResponse,
  TypeCallToActionWithoutUnresolvableLinksResponse,
  TypeDownloadableAssetWithoutUnresolvableLinksResponse,
  TypeDownloadableDocumentWithoutUnresolvableLinksResponse,
  TypeEventWithoutUnresolvableLinksResponse,
  TypeMarketingMaterialSetWithoutUnresolvableLinksResponse,
  TypeProductDetailsPageWithoutUnresolvableLinksResponse,
  TypeProductWithoutUnresolvableLinksResponse,
} from '~/types/contentful'

import type { TypeResultAssetData } from './SearchResultsAsset.vue'

const { $handleBlockLinkClick } = useNuxtApp()
const { localizedStrings } = useLocalizedStrings()

const cardLinkEl = useTemplateRef<ComponentPublicInstance | null>('cardLink')

const getLink = (path = '', contentTypeId = '') => {
  switch (contentTypeId) {
    case 'Article':
      return `news/${path}`
    case 'ProductDetailsPage':
      return `products/${path}`
    case 'Event':
      return `event/${path}`
    default:
      return path
  }
}

const props = withDefaults(
  defineProps<{
    type?:
      | 'article'
      | 'product'
      | 'event'
      | 'marketing-material'
      | 'document'
      | 'unset'
    data:
      | TypeArticleWithoutUnresolvableLinksResponse
      | TypeProductDetailsPageWithoutUnresolvableLinksResponse
      | TypeResultAssetData
      | TypeEventWithoutUnresolvableLinksResponse
      | TypeMarketingMaterialSetWithoutUnresolvableLinksResponse
      | TypeDownloadableDocumentWithoutUnresolvableLinksResponse
      | Entry<EntrySkeletonType, 'WITHOUT_UNRESOLVABLE_LINKS', string>
      | undefined
    variant?: 'default' | 'carousel'
  }>(),
  {
    type: 'unset',
    variant: 'default',
  },
)

const open = ref(false)

const computedContentType = computed(() => {
  const contentTypeId = props.data?.sys?.contentType?.sys?.id

  return (
    localizedStrings.value[uppercaseFirstLetter(contentTypeId || '')] ||
    contentTypeId
  )
})

const computedLink = computed(() => {
  let link: string | undefined | boolean = false
  if (instanceOfArticle(props.data)) link = props.data?.fields?.path
  if (instanceOfProductDetailsPage(props.data)) link = props.data?.fields?.path
  if (instanceOfEvent(props.data)) link = '/event/' + props.data?.fields?.path
  return link ? getLink(link, computedContentType.value) : link
})

const computedImage = computed(() => {
  let image: Asset<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined = undefined
  if (instanceOfArticle(props.data)) image = props.data?.fields?.featuredImage
  if (instanceOfProductDetailsPage(props.data))
    image = props.data?.fields?.media
  if (instanceOfDownloadableAsset(props.data)) image = props.data?.fields?.image
  if (instanceOfEvent(props.data)) image = props.data?.fields?.thumbnail
  if (instanceOfMarketingMaterialSet(props.data))
    image = props.data?.fields?.assets?.[0]?.fields?.image
  if (instanceOfDownloadableDocument(props.data))
    image = props.data?.fields?.image
  return image
})

const computedReleaseDate = computed(() => {
  let date: Date | undefined = undefined
  if (
    instanceOfArticle(props.data) &&
    !props.data?.fields?.hidePublishedDateTime
  )
    date = new Date(props.data?.fields?.publishedDateTime || '')
  if (instanceOfProductDetailsPage(props.data))
    date = new Date(props.data?.fields?.releaseDate?.split('T')?.[0] || '')

  let formatedDate = date ? format(new Date(date), 'LLL d, yyyy') : date

  if (computedContentType.value === 'Product') {
    formatedDate = `${localizedStrings.value['release-date']} ${formatedDate}`
  }

  return formatedDate
})

const computedTitle = computed(() => {
  let title: string | undefined = ''
  if (instanceOfArticle(props.data)) title = props.data?.fields?.title
  if (instanceOfProductDetailsPage(props.data)) title = props.data?.fields?.name
  if (instanceOfEvent(props.data)) title = props.data?.fields?.title
  if (instanceOfMarketingMaterialSet(props.data))
    title = props.data?.fields?.name
  if (instanceOfDownloadableDocument(props.data))
    title = props.data?.fields?.title
  if (instanceOfDownloadableAsset(props.data)) title = props.data?.fields?.title
  return title
})

const computedExcerpt = computed(() => {
  let excerpt: string | undefined = ''
  if (instanceOfArticle(props.data)) excerpt = props.data?.fields?.excerpt
  return excerpt
})

const computedStartDatetime = computed(() => {
  let startDatetime: string | undefined = ''
  if (instanceOfEvent(props.data))
    startDatetime = props.data?.fields?.startDatetime
  return startDatetime
})

const computedEndDatetime = computed(() => {
  let endDatetime: string | undefined = ''
  if (instanceOfEvent(props.data)) endDatetime = props.data?.fields?.endDatetime
  return endDatetime
})

const computedCta = computed(() => {
  let cta: TypeCallToActionWithoutUnresolvableLinksResponse | undefined =
    undefined
  if (instanceOfDownloadableDocument(props.data)) cta = props.data?.fields?.cta
  if (instanceOfDownloadableAsset(props.data)) cta = props.data?.fields?.cta
  return cta
})

const computedFit = computed((): 'pad' | 'thumb' => {
  if (
    computedContentType.value &&
    ['Product', 'MarketingMaterialSet', 'DownloadableDocument'].includes(
      computedContentType.value,
    )
  ) {
    return 'pad'
  }

  return 'thumb'
})

const computedItems = computed(() => {
  let items:
    | (
        | TypeDownloadableAssetWithoutUnresolvableLinksResponse
        | TypeProductWithoutUnresolvableLinksResponse
        | undefined
      )[]
    | undefined = undefined
  if (instanceOfMarketingMaterialSet(props.data))
    items = props.data?.fields?.assets
  if (instanceOfProductDetailsPage(props.data))
    items = props.data?.fields?.products

  return items?.map((item) => {
    let title: undefined | string = ''
    let image: Asset<'WITHOUT_UNRESOLVABLE_LINKS', string> | undefined =
      undefined
    let cta: TypeCallToActionWithoutUnresolvableLinksResponse | undefined =
      undefined
    let link: string | undefined = undefined

    // Downloadable Asset
    if (instanceOfDownloadableAsset(item)) {
      title = item?.fields?.title
      image = item?.fields?.image
      cta = item?.fields?.cta
    }

    // Product Details Page
    if (instanceOfProductDetailsPage(props.data) && instanceOfProduct(item)) {
      title = item?.fields?.name
      image = item?.fields?.images?.[0]
      link = `${getLink(
        props.data?.fields?.path,
        computedContentType.value,
      )}?product=${item?.sys?.id}`
    }

    return {
      sys: {
        id: item?.sys?.id,
        contentType: {
          sys: {
            id: item?.sys?.contentType?.sys?.id,
          },
        },
      },
      id: item?.sys?.id,
      title,
      image,
      cta,
      link,
    }
  })
})

const handleToggleDrawer = () => {
  open.value = !open.value
}
</script>

<style lang="scss" module>
@keyframes flowAppear {
  from {
    opacity: 0;
    transform: translateY(-10px);
  }

  to {
    opacity: 1;
    transform: none;
  }
}

.card {
  border-radius: 8px;
  overflow: hidden;

  background: #fff;
  height: 100%;

  position: relative;

  &::before {
    content: '';
    position: absolute;
    inset: 0;
    border: 1px solid var(--gray-4);
    border-radius: 8px;
    pointer-events: none;
  }

  &:hover,
  &:focus,
  &:focus-within {
    filter: brightness(0.97);
  }
}

.content {
  // border: 1px solid var(--gray-4);
  display: grid;
  grid-template-columns: 1fr 2fr;

  @include media('sm') {
    grid-template-columns: 317px 1fr;
  }

  .carousel & {
    display: flex;
    flex-direction: column;
    height: 100%;
  }
}

.contentLink {
  cursor: pointer;
}

.thumbnail {
  background: var(--gray-5);
}

.thumbnailPadded {
  padding: 5% 6.6%;
}

.inner {
  padding: 15px;
  position: relative;

  @include media('lg') {
    padding: 24px;
  }
}

.toggle {
  width: 32px;
  height: 32px;
  background: var(--purple);
  border: 0;
  border-radius: 50%;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #fff;
  position: absolute;
  top: 20px;
  right: 20px;

  @include media('sm') {
    right: 45px;
  }

  &:hover,
  &:focus {
    background: var(--purple-light);
  }
}

.toggleActive {
  transform: scale(1, -1);
}

.toggleIcon {
  height: 6px;
  width: 11px;
}

.meta {
  color: var(--purple);
  font-weight: bold;
  text-transform: uppercase;
  margin-bottom: 8px;
}

.title {
  overflow: hidden;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  margin-bottom: 3px;
}

.titleLink {
  color: inherit;
  text-decoration: none;

  &:hover,
  &:focus {
    text-decoration: none;
  }
}

.copy {
  color: var(--gray-8);
  font-size: 16px;
  line-height: 1.5;
  margin-top: 0.5em;

  word-break: break-word;
  text-overflow: ellipsis;
  -webkit-line-clamp: 3;
  line-clamp: 3;
  -webkit-box-orient: vertical;

  display: -webkit-box;
  height: calc(1.5em * 3);
  overflow: hidden;
}

.cta {
  margin-top: 20px;
}

.summary {
  @include sr-only();
}

.flow {
  details[open] & {
    animation: flowAppear 0.3s ease-out;
  }
}

.assets {
  border: solid var(--gray-4);
  border-width: 0 1px 1px;
  list-style: none;
  padding: 12px;
  margin: 0;
  display: grid;
  grid-template-columns: 1fr;
  gap: 10px;

  @include media('xs') {
    grid-template-columns: 1fr 1fr;
  }

  @include media('lg') {
    grid-template-columns: 1fr 1fr 1fr;
  }
}
</style>
